import { memo, useEffect, useState, useMemo } from 'react';
import useApi from '../../api';

const calendarUrlCache = new Map<string, string>();

const ChiliPiperWidget = memo(({ email, session_id, gettingFullResponse, config }: { 
    email: string, 
    session_id: string, 
    gettingFullResponse: boolean,
    config: any
}) => {
    const { post } = useApi();
    const [calendarUrl, setCalendarUrl] = useState<string>(() => {
        // Initialize from cache if available
        const cacheKey = `${email}-${session_id}`;
        return calendarUrlCache.get(cacheKey) || '';
    });

    const hexToRgb = (hex: string) => {
        hex = hex.replace(/^#/, '');
        let bigint = parseInt(hex, 16);
        let r = (bigint >> 16) & 255;
        let g = (bigint >> 8) & 255;
        let b = bigint & 255;
        return `${r}, ${g}, ${b}`;
    };

    useEffect(() => {
        const cacheKey = `${email}-${session_id}`;
        
        if (!gettingFullResponse && !calendarUrl && !calendarUrlCache.has(cacheKey)) {
            const fetchCalendarUrl = async () => {
                try {
                    const response = await post('/integrations/chili-piper/url/', {
                        session_id,
                        email
                    });
                    const url = response.data.url;
                    setCalendarUrl(url);
                    calendarUrlCache.set(cacheKey, url);
                } catch (error) {
                    console.error('Error fetching ChiliPiper calendar:', error);
                }
            };

            fetchCalendarUrl();
        }
    }, [session_id, email, gettingFullResponse, calendarUrl]);

    if (gettingFullResponse || !calendarUrl) {
        return (
            <div className="flex items-center justify-center my-4">
                <div className="flex items-center space-x-2 my-2 p-2 rounded-lg"
                    style={{
                        backgroundColor: `rgba(${hexToRgb(config.color)}, 0.03)`,
                        border: `1px solid rgba(${hexToRgb(config.color)}, 0.1)`,
                        color: `rgba(${hexToRgb(config.color)}, 0.7)`
                    }}
                >
                    <div className="inline-flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" 
                            xmlns="http://www.w3.org/2000/svg" 
                            fill="none" 
                            viewBox="0 0 24 24"
                            style={{
                                color: `rgba(${hexToRgb(config.color)}, 0.7)`
                            }}
                        >
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading calendar...
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div 
            className="min-w-[200px] h-[950px]"
        >
            <iframe
                src={calendarUrl}
                width="100%"
                height="100%"
                frameBorder="0"
            />
        </div>
    );
});

export default memo(ChiliPiperWidget, (prevProps, nextProps) => {
    return prevProps.email === nextProps.email && 
           prevProps.session_id === nextProps.session_id &&
           prevProps.gettingFullResponse === nextProps.gettingFullResponse;
});